import { useState, useEffect } from "react";
import { publishMulti } from "../api/api";

const PLATFORM_OPTIONS = [
  { key: "facebook", label: "Facebook", color: "bg-blue-100 text-blue-800" },
  { key: "instagram", label: "Instagram", color: "bg-pink-100 text-pink-800" },
  { key: "linkedin", label: "LinkedIn", color: "bg-gray-100 text-gray-800" },
  { key: "telegram", label: "Telegram", color: "bg-cyan-100 text-cyan-800" }
];

export default function PostForm({ user }) {
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [platforms, setPlatforms] = useState(["facebook"]);
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hashtags, setHashtags] = useState([]);
  const [selectedHashtags, setSelectedHashtags] = useState([]);

  // Auto clear status
  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => setStatus(""), 8000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // Generate hashtags from message
  useEffect(() => {
    const words = message.split(/\s+/)
      .filter(w => w.length > 3 && /^[a-zA-Z]+$/.test(w))
      .slice(0, 5); // max 5 hashtags
    setHashtags(words.map(w => `#${w}`));
  }, [message]);

  const toggle = (key) =>
    setPlatforms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);

  // Click to add/remove individual hashtag
  const toggleHashtag = (tag) => {
    setSelectedHashtags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setStatus("❌ Message is required");
      return;
    }

    if (platforms.includes("instagram") && !imageUrl.trim()) {
      setStatus("❌ Instagram requires an image URL");
      return;
    }

    setSubmitting(true);
    setStatus("Submitting...");

    // Append selected hashtags to message before sending
    const finalMessage = message + (selectedHashtags.length > 0 ? " " + selectedHashtags.join(" ") : "");

    try {
      await publishMulti({
        message: finalMessage,
        link,
        imageUrl,
        platforms,
        scheduleTime,
        userToken: user?.token
      });

      setStatus(scheduleTime ? "✅ Scheduled!" : "✅ Published!");
      setMessage(""); setLink(""); setImageUrl(""); setScheduleTime("");
      setPlatforms(["facebook"]); setSelectedHashtags([]);
    } catch (err) {
      console.error(err);
      setStatus("❌ Error — check console");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto mt-10 p-6 bg-blue-600 rounded-xl text-white shadow-lg">
      <h2 className="text-center text-2xl mb-6 font-bold">Multi-Platform Publisher</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder="Enter your message"
          className="w-full p-2 rounded-md text-black"
        />

        {hashtags.length > 0 && (
          <div className="mb-2">
            <span className="text-sm">Suggested Hashtags:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {hashtags.map((tag, i) => (
                <span
                  key={i}
                  onClick={() => toggleHashtag(tag)}
                  className={`px-2 py-1 rounded-md cursor-pointer ${selectedHashtags.includes(tag) ? "bg-green-500 text-white" : "bg-white text-black"}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Link (optional)"
          className="w-full p-2 rounded-md text-black"
        />
        <input
          type="url"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="Image URL (required for Instagram)"
          className="w-full p-2 rounded-md text-black"
        />
        <input
          type="datetime-local"
          value={scheduleTime}
          onChange={(e) => setScheduleTime(e.target.value)}
          className="w-full p-2 rounded-md text-black"
        />

        <div className="flex flex-wrap gap-2">
          {PLATFORM_OPTIONS.map((p) => (
            <label
              key={p.key}
              className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer ${platforms.includes(p.key) ? p.color : "bg-white text-black"}`}
            >
              <input
                type="checkbox"
                checked={platforms.includes(p.key)}
                onChange={() => toggle(p.key)}
              />
              {p.label}
            </label>
          ))}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2 bg-green-500 rounded-md font-bold disabled:opacity-50"
        >
          {scheduleTime ? "Schedule" : "Publish"}
        </button>
      </form>

      {status && <p className="mt-4 text-center">{status}</p>}

      <h3 className="mt-6 text-lg font-semibold">Live Preview:</h3>
      {platforms.map((p) => (
        <div
          key={p}
          className={`p-3 rounded-lg mt-2 ${PLATFORM_OPTIONS.find(pl => pl.key === p).color}`}
        >
          <strong>{p.toUpperCase()} Preview</strong>
          <p>{message + (selectedHashtags.length > 0 ? " " + selectedHashtags.join(" ") : "") || "No message yet"}</p>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="underline">
              {link}
            </a>
          )}
          {imageUrl && <img src={imageUrl} alt="Preview" className="mt-2 rounded-md max-w-full" />}
          {scheduleTime && <p>Scheduled: {new Date(scheduleTime).toLocaleString()}</p>}
        </div>
      ))}
    </div>
  );
}
